import {
  IdleAnimation,
  IdleAnimationStage,
  delay,
  waitForAnimationEnd
} from './idle-animation';
import './space-encounter-animation.css';

// Flat single-color silhouettes (fill: currentColor), authored small so they
// read cleanly at silhouette scale on the full-page stage.

// Human shuttle, nose pointing LEFT (the direction it will flee). A sleeker
// silhouette: tapered fuselage, swept-back wing, tail fin, cockpit window and a
// twin engine nozzle at the rear.
const SHUTTLE_SVG = `
<svg viewBox="0 0 120 48" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <path d="M4 24 Q22 14 60 15 L100 16 Q116 18 116 24 Q116 30 100 32 L60 33 Q22 34 4 24 Z"/>
  <path d="M70 15 L96 3 Q102 3 100 9 L86 16 Z"/>
  <path d="M70 33 L96 45 Q102 45 100 39 L86 32 Z"/>
  <path d="M100 16 L112 12 Q118 14 116 20 L104 22 Z" opacity="0.85"/>
  <path d="M18 20 Q30 16 44 18 Q30 22 18 24 Z" fill="rgba(10,16,28,0.85)"/>
  <circle cx="30" cy="24" r="4" fill="rgba(120,180,240,0.9)"/>
  <rect x="112" y="21" width="8" height="6" rx="2" fill="rgba(150,200,255,0.8)"/>
</svg>`;

// Astronaut silhouette: helmet with a visor highlight, a life-support backpack,
// a chest control panel, and softly posed arms/legs. Authored small so it reads
// cleanly at silhouette scale.
const ASTRONAUT_SVG = `
<svg viewBox="0 0 36 44" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <rect x="9" y="6" width="18" height="20" rx="8" fill="rgba(170,190,220,0.7)"/>
  <rect x="6" y="20" width="24" height="16" rx="7"/>
  <circle cx="18" cy="13" r="8"/>
  <path d="M13 11 Q18 7 23 11 Q18 12 13 11 Z" fill="rgba(150,200,255,0.85)"/>
  <rect x="14" y="24" width="8" height="6" rx="2" fill="rgba(10,16,28,0.85)"/>
  <circle cx="16" cy="27" r="1.1" fill="rgba(120,220,160,0.95)"/>
  <circle cx="20" cy="27" r="1.1" fill="rgba(255,170,120,0.95)"/>
  <rect x="2" y="23" width="6" height="4" rx="2"/>
  <rect x="28" y="23" width="6" height="4" rx="2"/>
  <rect x="11" y="35" width="5" height="8" rx="2"/>
  <rect x="20" y="35" width="5" height="8" rx="2"/>
</svg>`;

// Alien warship — a capital-ship silhouette in the spirit of a Star Trek /
// Battlestar Galactica cruiser rather than a saucer. It travels LEFT (enters
// from the right), so the command prow points left and the engine block with
// glowing nacelles sits at the rear (right). A long armoured hull with a raised
// command tower, ventral keel, and twin engine nacelles reads as a warship.
const ALIEN_MOTHERSHIP_SVG = `
<svg viewBox="0 0 110 46" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <path d="M4 24 L20 18 L86 16 Q98 17 104 22 L104 26 Q98 31 86 32 L20 30 Z"/>
  <path d="M28 16 L46 8 L74 9 L70 16 Z" opacity="0.9"/>
  <path d="M40 9 L44 3 L60 3 L64 9 Z" opacity="0.85"/>
  <path d="M30 32 L54 32 L48 40 L36 40 Z" opacity="0.8"/>
  <path d="M86 16 L104 12 L108 18 L100 22 Z" opacity="0.85"/>
  <path d="M86 32 L104 36 L108 30 L100 26 Z" opacity="0.85"/>
  <path d="M20 20 L84 19" stroke="rgba(10,20,24,0.55)" stroke-width="1.3"/>
  <path d="M20 28 L84 27" stroke="rgba(10,20,24,0.4)" stroke-width="1"/>
  <rect x="34" y="5" width="24" height="2.4" rx="1" fill="rgba(150,255,215,0.6)"/>
  <circle cx="8" cy="24" r="2.4" fill="rgba(180,255,225,0.95)"/>
  <circle cx="100" cy="19" r="3.2" fill="rgba(150,255,215,0.95)"/>
  <circle cx="100" cy="19" r="6" fill="rgba(150,255,215,0.28)"/>
  <circle cx="100" cy="29" r="3.2" fill="rgba(150,255,215,0.95)"/>
  <circle cx="100" cy="29" r="6" fill="rgba(150,255,215,0.28)"/>
</svg>`;

// Smaller escort cruiser — same warship language as the mothership but a leaner
// single-nacelle frigate silhouette for the flanking ships of the fleet.
const ALIEN_ESCORT_SVG = `
<svg viewBox="0 0 90 34" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <path d="M4 18 L16 13 L70 12 Q82 13 86 17 L86 19 Q82 23 70 24 L16 23 Z"/>
  <path d="M24 12 L40 6 L60 7 L56 12 Z" opacity="0.9"/>
  <path d="M26 24 L46 24 L40 30 L30 30 Z" opacity="0.8"/>
  <path d="M70 12 L86 9 L88 14 L82 17 Z" opacity="0.85"/>
  <path d="M16 15 L68 14" stroke="rgba(10,20,24,0.5)" stroke-width="1.1"/>
  <circle cx="7" cy="18" r="2" fill="rgba(180,255,225,0.95)"/>
  <circle cx="82" cy="14" r="2.6" fill="rgba(150,255,215,0.95)"/>
  <circle cx="82" cy="14" r="5" fill="rgba(150,255,215,0.28)"/>
</svg>`;

/**
 * A short narrative "space encounter" idle animation played on the full-page
 * stage: a human shuttle descends to a fixed lower-center rest spot, a tethered
 * astronaut trails out behind it, an alien fleet (a mothership plus escort
 * cruisers) arrives from the right, and when it gets close the shuttle bolts
 * left ALONE — the tether snaps and the astronaut is left stranded behind.
 *
 * A third entry in the {@link IdleAnimationDirector} registry. Like the parade
 * and shooting stars it is entirely CSS-keyframe driven; the choreography is
 * sequenced here with {@link waitForAnimationEnd} and {@link delay}, and every
 * node created is removed once the pass finishes (or is aborted).
 */
export class SpaceEncounterAnimation implements IdleAnimation {
  readonly name = 'space-encounter';
  // Narrative effect that uses the whole background, like the shooting stars.
  readonly stage: IdleAnimationStage = 'page';

  async play(stage: HTMLElement, signal: AbortSignal): Promise<void> {
    if (signal.aborted) {
      return;
    }

    const shuttle = document.createElement('div');
    shuttle.className = 'space-actor space-shuttle';
    shuttle.innerHTML = SHUTTLE_SVG;

    const astronaut = document.createElement('div');
    astronaut.className = 'space-actor space-astronaut';
    astronaut.innerHTML = ASTRONAUT_SVG;

    const tether = document.createElement('div');
    tether.className = 'space-tether';

    // Surprise mark that pops beside the stranded astronaut after the shuttle
    // bolts. Hidden until phase 5.
    const surprise = document.createElement('div');
    surprise.className = 'space-surprise';
    surprise.textContent = '!';

    // Alien fleet — one mothership at full size plus a few smaller escort
    // cruisers flanking it. Each ship gets a modifier class so the CSS can
    // stagger its rest offset and approach timing into a loose formation.
    const fleet = [
      { svg: ALIEN_MOTHERSHIP_SVG, cls: 'space-alien--mothership' },
      { svg: ALIEN_ESCORT_SVG, cls: 'space-alien--escort-1' },
      { svg: ALIEN_ESCORT_SVG, cls: 'space-alien--escort-2' },
      { svg: ALIEN_ESCORT_SVG, cls: 'space-alien--escort-3' }
    ];
    const aliens = fleet.map(({ svg, cls }) => {
      const el = document.createElement('div');
      el.className = `space-actor space-alien ${cls}`;
      el.innerHTML = svg;
      return el;
    });

    // Tether sits behind the shuttle/astronaut in the append order.
    stage.append(tether, shuttle, astronaut, surprise, ...aliens);

    try {
      // Phase 1 — shuttle descends and fades in to the rest spot.
      shuttle.classList.add('space-shuttle--descend');
      await waitForAnimationEnd(shuttle, 'space-shuttle-descend', signal);
      if (signal.aborted) {
        return;
      }

      // Phase 2 — astronaut trails out behind the shuttle, tether extends.
      astronaut.classList.add('space-astronaut--out');
      tether.classList.add('space-tether--out');
      await waitForAnimationEnd(astronaut, 'space-astronaut-out', signal);
      if (signal.aborted) {
        return;
      }

      // Phase 3 — hold a beat before the alien shows up.
      await delay(1000, signal);
      if (signal.aborted) {
        return;
      }

      // Phase 4 — the alien fleet slides in from the right and approaches. The
      // mothership leads; escorts trail slightly (staggered via CSS). Wait on
      // the mothership's approach (the reference ship) before continuing.
      aliens.forEach(el => el.classList.add('space-alien--approach'));
      await waitForAnimationEnd(aliens[0], 'space-alien-approach', signal);
      if (signal.aborted) {
        return;
      }

      // Beat — the fleet has fully stopped; hold a moment so its arrival reads
      // before the shuttle reacts and bolts.
      await delay(600, signal);
      if (signal.aborted) {
        return;
      }

      // Phase 5 — the shuttle bolts left ALONE, abandoning the astronaut. The
      // tether snaps and the shuttle detaches; the astronaut and (limp) tether
      // stay put where they were while the fleet recoils. Wait on the shuttle's
      // flight (now the longest segment) so the escape finishes before teardown.
      shuttle.classList.add('space-shuttle--flee');
      tether.classList.add('space-tether--snap');
      surprise.classList.add('space-surprise--pop');
      aliens.forEach(el => el.classList.add('space-alien--recoil'));
      await waitForAnimationEnd(shuttle, 'space-shuttle-flee', signal);
      if (signal.aborted) {
        return;
      }

      // Phase 6 — let the stranded astronaut (and its surprise mark) linger a
      // beat so the abandonment lands, then fade them out before teardown.
      await delay(1400, signal);
      if (signal.aborted) {
        return;
      }
      astronaut.classList.add('space-astronaut--strand');
      surprise.classList.add('space-surprise--fade');
      await waitForAnimationEnd(astronaut, 'space-astronaut-strand', signal);
    } finally {
      tether.remove();
      shuttle.remove();
      astronaut.remove();
      surprise.remove();
      aliens.forEach(el => el.remove());
    }
  }
}
